import { Component, ChangeDetectionStrategy, input, computed, output } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { Estimate, VendorOrder } from '../../../../../../shared/models/vendor-order.model';
import { VendorSelectBtnComponent } from '../../../../../../components/vendor-select-btn/vendor-select-btn.component';

const STATUS_MAP: Record<string, { label: string; cssClass: string }> = {
  AWAITING_ESTIMATION: { label: 'Pending quote estimates', cssClass: 'awaiting-estimation' },
  VENDOR_CREATED: { label: 'Pending quote estimates', cssClass: 'awaiting-estimation' },
  AWAITING_VENDOR_SELECTION: { label: 'Approved', cssClass: 'awaiting-selection' },
  AWAITING_VENDOR_APPROVAL: { label: 'Awaiting Vendor Approval', cssClass: 'awaiting-selection' },
  ON_HOLD: { label: 'On Hold', cssClass: 'on-hold' },
  REJECTED: { label: 'Rejected', cssClass: 'rejected' },
  NOT_AVAILABLE: { label: 'Not Available', cssClass: 'rejected' },
  CANCELLED: { label: 'Cancelled', cssClass: 'rejected' },
  COMPLETED: { label: 'Completed', cssClass: 'completed' },
  IN_PROGRESS: { label: 'In Progress', cssClass: 'in-progress' },
};

@Component({
  selector: 'app-quote-card',
  standalone: true,
  imports: [CurrencyPipe, VendorSelectBtnComponent],
  templateUrl: './quote-card.component.html',
  styleUrl: './quote-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuoteCardComponent {
  readonly estimate = input.required<Estimate>();
  readonly orderDeadline = input<string>();
  readonly isLowestStandard = input(false);
  readonly order = input.required<VendorOrder>();
  readonly viewDetails = output<Estimate>();
  readonly quoteConfirmed = output<string>();

  readonly vendorName = computed(() => this.estimate().name ?? 'Unknown Vendor');

  readonly statusLabel = computed(() => {
    const status = this.estimate().jobStatus ?? '';
    return STATUS_MAP[status]?.label ?? status;
  });

  readonly statusClass = computed(() => {
    const status = this.estimate().jobStatus ?? '';
    return STATUS_MAP[status]?.cssClass ?? 'awaiting-estimation';
  });

  readonly hasVendorSelection = computed(() =>
    this.order().estimates?.some(e => e.jobStatus === 'AWAITING_VENDOR_SELECTION') ?? false
  );

  readonly hasVendorRejection = computed(() =>
    this.order().estimates?.some(e => e.jobStatus === 'REJECTED') ?? false
  );

  readonly responseDeadline = computed(() => {
    const dl = this.orderDeadline() ?? this.estimate().deadline;
    if (!dl) return '--';
    return this.formatDateDDMonYYYY(dl);
  });

  readonly rows = computed<any[]>(() => {
    const est = this.estimate();
    const result: any[] = [];

    const currency = est.currency ?? est.quote?.normal?.currency ?? 'USD';

    if (est.quote?.normal) {
      const deliveryDate = est.estimatedDeliveryDates?.normal?.estimatedDeliveryDate ?? est.deadline;
      result.push({
        jobType: 'Standard',
        deliveryDate: deliveryDate ? this.formatDateDDMonYYYY(deliveryDate) : '--',
        estimatedCost: est.quote.normal.totalCost ?? 0,
        currency,
        consideration: this.getConsideration('Standard', deliveryDate),
        considerationIcon: this.getConsiderationIcon('Standard', deliveryDate),
      });
    }

    if (est.quote?.rushed) {
      const deliveryDate = est.estimatedDeliveryDates?.rushed?.estimatedDeliveryDate ?? est.deadline;
      result.push({
        jobType: 'Rushed',
        deliveryDate: deliveryDate ? this.formatDateDDMonYYYY(deliveryDate) : '--',
        estimatedCost: est.quote.rushed.totalCost ?? 0,
        currency,
        consideration: this.getConsideration('Rushed', deliveryDate),
        considerationIcon: this.getConsiderationIcon('Rushed', deliveryDate),
      });
    }

    if (!est.quote) {
      const deliveryDate = est.deadline;
      result.push({
        jobType: 'Standard',
        deliveryDate: deliveryDate ? this.formatDateDDMonYYYY(deliveryDate) : '--',
        estimatedCost: est.priceMax ?? est.priceMin,
        currency,
        consideration: this.getConsideration('Standard', deliveryDate),
        considerationIcon: this.getConsiderationIcon('Standard', deliveryDate),
      });
    }

    return result;
  });

  private getConsideration(jobType: string, deliveryDate?: string): string | null {
    if (jobType === 'Standard' && this.isLowestStandard()) {
      return 'Lowest Standard Quote';
    }
    if (deliveryDate && this.flagDeadline(deliveryDate)) {
      return 'Does Not Meet Target Date';
    }
    return null;
  }

  private getConsiderationIcon(jobType: string, deliveryDate?: string): 'lowest' | 'warning' | null {
    if (jobType === 'Standard' && this.isLowestStandard()) {
      return 'lowest';
    }
    if (deliveryDate && this.flagDeadline(deliveryDate)) {
      return 'warning';
    }
    return null;
  }

  private flagDeadline(estimatedDeliveryDate: string): boolean {
    const deadline = this.orderDeadline();
    if (!deadline) return false;
    const dDeadline = new Date(Date.parse(deadline)).setHours(0, 0, 0, 0);
    const dEstimated = new Date(Date.parse(estimatedDeliveryDate)).setHours(0, 0, 0, 0);
    return dDeadline < dEstimated;
  }

  private formatDateDDMonYYYY(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '--';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate().toString().padStart(2, '0');
    return `${day}/${months[date.getMonth()]}/${date.getFullYear()}`;
  }

  shouldShowDetailsBtn(): boolean {
    const showDetailsBtn = this.statusLabel() === 'Approved'
    return showDetailsBtn;
  }
}
